import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Platform,
  Switch,
  useColorScheme as useDeviceColorScheme,
  ActivityIndicator,
} from "react-native";
import axios from "axios";
import { useNavigation } from "@react-navigation/native";
import { Feather, AntDesign } from "@expo/vector-icons";
import { getDocs, query, collection, where } from "firebase/firestore";
import { FIREBASE_DB, FIREBASE_AUTH } from "../../FirebaseConfig";
import moment from "moment-timezone";
import CustomAlert from "./CustomAlert";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

// Add this function before the EmployeeProfile component
const generatePassword = () => {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let password = "";
  for (let i = 0; i < 12; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

const parseOptionalNumber = (value) => {
  const trimmed = `${value ?? ""}`.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const BackButton = ({ color }) => {
  const navigation = useNavigation();
  return (
    <TouchableOpacity
      style={styles.backButton}
      onPress={() => navigation.goBack()}
    >
      <Feather name="arrow-left" size={24} color={color} />
    </TouchableOpacity>
  );
};

const EmployeeProfile = ({ route }) => {
  const navigation = useNavigation();
  const deviceColorScheme = useDeviceColorScheme();
  const [colorScheme, setColorScheme] = useState(deviceColorScheme);

  if (!route || !route.params) {
    return <Text>Error: No route parameters provided.</Text>;
  }

  const { employee } = route.params;
  const [editedEmployee, setEditedEmployee] = useState({
    ...employee,
    isSpecialFormatEmployee: employee?.isSpecialFormatEmployee === true,
  });
  const [isReminderEnabled, setIsReminderEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unregisteredUsers, setUnregisteredUsers] = useState([]);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);
  const [alertConfig, setAlertConfig] = useState({
    visible: false,
    title: "",
    message: "",
    onConfirm: null,
    showCancel: false,
  });

  useEffect(() => {
    if (!editedEmployee.supervisors) {
      setEditedEmployee((prev) => ({ ...prev, supervisors: [] }));
    }
    fetchReminderEmailStatus();
    checkRegistrationStatus();
  }, []);

  const fetchReminderEmailStatus = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `https://sbg-backend.onrender.com/employees/${editedEmployee.employeeId}/reminder-emails`
      );
      setIsReminderEnabled(response.data.reminderEmailsEnabled);
    } catch (error) {
      console.error("Error fetching reminder email status:", error);
      showAlert(
        "Error",
        "Failed to fetch reminder email status. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleReminderEmails = async (value) => {
    setIsLoading(true);
    try {
      await axios.put(
        `https://sbg-backend.onrender.com/employees/${editedEmployee.employeeId}/reminder-emails`,
        { enabled: value }
      );
      setIsReminderEnabled(value);
      showAlert(
        "Success",
        `Reminder emails have been ${value ? "enabled" : "disabled"}.`
      );
    } catch (error) {
      console.error("Error updating reminder email status:", error);
      showAlert(
        "Error",
        "Failed to update reminder email status. Please try again later."
      );
      setIsReminderEnabled(!value);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleColorScheme = () => {
    setColorScheme((prevScheme) => (prevScheme === "light" ? "dark" : "light"));
  };

  const handleEdit = (key, value) => {
    setEditedEmployee((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSupervisorEdit = (index, field, value) => {
    setEditedEmployee((prevState) => {
      const newSupervisors = [...prevState.supervisors];
      newSupervisors[index] = { ...newSupervisors[index], [field]: value };
      return { ...prevState, supervisors: newSupervisors };
    });
  };

  const addSupervisor = () => {
    setEditedEmployee((prevState) => ({
      ...prevState,
      supervisors: [...prevState.supervisors, { id: "", email: "" }],
    }));
  };

  const removeSupervisor = (index) => {
    setEditedEmployee((prevState) => ({
      ...prevState,
      supervisors: prevState.supervisors.filter((_, i) => i !== index),
    }));
  };

  const saveChanges = async () => {
    try {
      setIsLoading(true);
      
      // Email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      // Safely filter out empty supervisors and validate emails
      const filteredSupervisors = (editedEmployee.supervisors || []).filter(
        (supervisor) => {
          if (!supervisor || !supervisor.id || !supervisor.email) return false;
          
          const trimmedId = supervisor.id.trim();
          const trimmedEmail = supervisor.email.trim();
          
          if (trimmedId === "" || trimmedEmail === "") return false;
          
          // Debug logging for email validation
          console.log('Validating supervisor email:', {
            id: trimmedId,
            originalEmail: supervisor.email,
            trimmedEmail: trimmedEmail,
            containsSpaces: trimmedEmail.includes(' '),
            bytes: [...trimmedEmail].map(c => c.charCodeAt(0)),
            length: trimmedEmail.length
          });
          
          // Validate email format
          if (!emailRegex.test(trimmedEmail)) {
            showAlert("Error", `Invalid email format for supervisor: ${trimmedId}`);
            throw new Error(`Invalid email format: ${trimmedEmail}`);
          }
          
          return true;
        }
      );

      // Backend will handle supervisor creation and welcome emails
      // Use the new endpoint that handles supervisor creation and welcome emails
      await axios.put(
        `https://sbg-backend.onrender.com/editEmployeeWithSupervisors/${editedEmployee.employeeId}`,
        {
          ...editedEmployee,
          department: editedEmployee.department || "",
          payRate: parseOptionalNumber(editedEmployee.payRate),
          otPayRate: parseOptionalNumber(editedEmployee.otPayRate),
          stBillRate: parseOptionalNumber(editedEmployee.stBillRate),
          otBillRate: parseOptionalNumber(editedEmployee.otBillRate),
          isSpecialFormatEmployee: editedEmployee.isSpecialFormatEmployee === true,
          supervisorId: editedEmployee.supervisorId || filteredSupervisors[0]?.id || "",
          supervisors: filteredSupervisors,
        }
      );

      showAlert("Success", "Employee updated successfully", () => {
        navigation.navigate("Employee Database");
      });
    } catch (error) {
      console.error("Error updating employee:", error);
      showAlert("Error", "Failed to update employee");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteEmployee = () => {
    showAlert(
      "Confirm Deletion",
      "Are you sure you want to delete this employee?",
      performDelete,
      true
    );
  };

  const checkRegistrationStatus = async () => {
    setIsCheckingRegistration(true);
    try {
      const unregistered = [];
      const employeeEmail = editedEmployee.email;

      // Check employee registration
      const employeeSnapshot = await getDocs(
        query(
          collection(FIREBASE_DB, "users"),
          where("email", "==", employeeEmail)
        )
      );

      if (employeeSnapshot.empty) {
        unregistered.push({
          email: employeeEmail,
          name: editedEmployee.employeeName,
          type: "employee",
        });
      }

      // Check supervisors registration
      for (const supervisor of editedEmployee.supervisors) {
        if (supervisor.email) {
          const supervisorSnapshot = await getDocs(
            query(
              collection(FIREBASE_DB, "users"),
              where("email", "==", supervisor.email)
            )
          );

          if (supervisorSnapshot.empty) {
            unregistered.push({
              email: supervisor.email,
              name: supervisor.id,
              type: "supervisor",
            });
          }
        }
      }

      setUnregisteredUsers(unregistered);
    } catch (error) {
      console.error("Error checking registration status:", error);
    } finally {
      setIsCheckingRegistration(false);
    }
  };

  const handleRegisterUser = async (user) => {
    try {
      setIsLoading(true);
      const generatedPassword = generatePassword();

      // Make API call to your backend to create user
      const response = await axios.post(
        "https://sbg-backend.onrender.com/createUser",
        {
          email: user.email,
          password: generatedPassword,
          name: user.name,
          role: user.type,
        }
      );

      if (response.data.success) {
        showAlert(
          "Registration Success",
          `User registered successfully!\nCredentials:\nEmail: ${user.email}\nPassword: ${generatedPassword}`,
          async () => {
            try {
              await Clipboard.setStringAsync(generatedPassword);
              showAlert("Success", "Password copied to clipboard!");
              checkRegistrationStatus();
            } catch (error) {
              console.error("Failed to copy password:", error);
            }
          },
          true
        );
      }
    } catch (error) {
      console.error("Error registering user:", error);
      let errorMessage = "Failed to register user. Please try again.";
      if (error.response?.data?.code === "auth/email-already-in-use") {
        errorMessage = "This email is already registered.";
      }
      showAlert("Registration Failed", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderRegistrationButtons = () => {
    if (isCheckingRegistration) {
      return <ActivityIndicator size="small" color="#007AFF" />;
    }

    if (unregisteredUsers.length === 0) {
      return null;
    }

    return (
      <View style={styles.registrationContainer}>
        <Text
          style={[
            styles.registrationTitle,
            colorScheme === "dark" && styles.registrationTitleDark,
          ]}
        >
          Unregistered Users
        </Text>
        {unregisteredUsers.map((user, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.registrationButton,
              colorScheme === "dark" && styles.registrationButtonDark,
            ]}
            onPress={() => handleRegisterUser(user)}
          >
            <Text style={styles.registrationButtonText}>
              Register {user.email}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const showAlert = (title, message, onConfirm = null, showCancel = false) => {
    setAlertConfig({
      visible: true,
      title,
      message,
      onConfirm,
      showCancel,
    });
  };

  const performDelete = () => {
    axios
      .delete(
        `https://sbg-backend.onrender.com/deleteEmployee/${editedEmployee.employeeId}`
      )
      .then((response) => {
        console.log("Employee deleted successfully");
        if (Platform.OS === "web") {
          window.alert("The employee has been deleted successfully.");
        } else {
          showAlert(
            "Employee Deleted",
            "The employee has been deleted successfully.",
            [{ text: "OK", onPress: () => navigation.navigate("Employees") }],
            { cancelable: false }
          );
        }
        navigation.navigate("Employees");
      })
      .catch((error) => {
        console.error("Error deleting employee:", error.response?.data);
      });
  };

  // Function to send welcome email to the employee or supervisor
  const sendWelcomeEmail = async (recipient, isEmployee = true, supervisorIndex = null) => {
    try {
      setIsLoading(true);
      
      let recipientEmail = '';
      let recipientName = '';
      
      if (isEmployee) {
        recipientEmail = editedEmployee.email;
        recipientName = editedEmployee.employeeName;
      } else if (supervisorIndex !== null && editedEmployee.supervisors && editedEmployee.supervisors[supervisorIndex]) {
        recipientEmail = editedEmployee.supervisors[supervisorIndex].email;
        recipientName = editedEmployee.supervisors[supervisorIndex].id;
      } else {
        throw new Error('Invalid recipient');
      }
      
      // Make API call to send welcome email
      const response = await axios.post("https://sbg-backend.onrender.com/sendWelcomeEmail", {
        email: recipientEmail,
        employeeName: recipientName,
        isEmployee: isEmployee
      });
      
      setIsLoading(false);
      
      if (response.data && response.data.success) {
        showAlert(
          "Success",
          `Welcome email has been sent to ${recipientEmail}.`,
          null,
          false
        );
      } else {
        showAlert(
          "Error",
          response.data.message || "Failed to send welcome email. Please try again.",
          null,
          false
        );
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error sending welcome email:", error);
      showAlert(
        "Error",
        "Failed to send welcome email. Please try again.",
        null,
        false
      );
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, colorScheme === "dark" && styles.containerDark]}
    >
      <StatusBar
        barStyle={colorScheme === "dark" ? "light-content" : "dark-content"}
        backgroundColor={colorScheme === "dark" ? "#1E1E1E" : "#FFFFFF"}
      />

      <View
        style={[styles.header, colorScheme === "dark" && styles.headerDark]}
      >
        <BackButton color={colorScheme === "dark" ? "#FFFFFF" : "#000000"} />
        <Text
          style={[styles.title, colorScheme === "dark" && styles.titleDark]}
        >
          Employee Profile
        </Text>
        <TouchableOpacity onPress={toggleColorScheme}>
          <Feather
            name={colorScheme === "dark" ? "sun" : "moon"}
            size={24}
            color={colorScheme === "dark" ? "#FFFFFF" : "#000000"}
          />
        </TouchableOpacity>
      </View>

      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#2196F3" />
          <Text style={styles.loadingText}>Processing...</Text>
        </View>
      )}

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{ alignItems: "center" }}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.formContainer}>
          <View style={styles.formGrid}>
            {Object.entries(editedEmployee)
              .filter(([key]) =>
                ![
                  "_id",
                  "__v",
                  "supervisors",
                  "department",
                  "isSpecialFormatEmployee",
                  "payRate",
                  "otPayRate",
                  "stBillRate",
                  "otBillRate",
                ].includes(key)
              )
              .map(([key, value]) => (
                <View key={key} style={styles.formField}>
                  <Text
                    style={[
                      styles.label,
                      colorScheme === "dark" && styles.labelDark,
                    ]}
                  >
                    {key.charAt(0).toUpperCase() + key.slice(1)}:
                  </Text>
                  <TextInput
                    style={[
                      styles.input,
                      colorScheme === "dark" && styles.inputDark,
                    ]}
                    value={
                      key === "createdAt"
                        ? moment(value)
                            .tz("America/Phoenix")
                            .format("MMMM D, YYYY h:mm A [MST]")
                        : value?.toString() || ""
                    }
                    onChangeText={(text) => handleEdit(key, text)}
                    editable={!["employeeId", "createdAt"].includes(key)}
                    placeholderTextColor={
                      colorScheme === "dark" ? "#666" : "#999"
                    }
                  />
                </View>
              ))}
          </View>

          <View>
            <Text
              style={[styles.label, colorScheme === "dark" && styles.labelDark]}
            >
              Department:
            </Text>
            <TextInput
              style={[styles.input, colorScheme === "dark" && styles.inputDark]}
              value={editedEmployee.department || ""}
              onChangeText={(text) => handleEdit("department", text)}
              placeholder="Enter department"
              placeholderTextColor={colorScheme === "dark" ? "#666" : "#999"}
            />
          </View>

          <View style={styles.rateGrid}>
            <View style={styles.rateField}>
              <Text
                style={[styles.label, colorScheme === "dark" && styles.labelDark]}
              >
                Pay Rate:
              </Text>
              <TextInput
                style={[styles.input, colorScheme === "dark" && styles.inputDark]}
                value={editedEmployee.payRate?.toString() || ""}
                onChangeText={(text) => handleEdit("payRate", text)}
                placeholder="Enter pay rate"
                placeholderTextColor={colorScheme === "dark" ? "#666" : "#999"}
                keyboardType="decimal-pad"
              />
            </View>
            <View style={styles.rateField}>
              <Text
                style={[styles.label, colorScheme === "dark" && styles.labelDark]}
              >
                OT Pay Rate:
              </Text>
              <TextInput
                style={[styles.input, colorScheme === "dark" && styles.inputDark]}
                value={editedEmployee.otPayRate?.toString() || ""}
                onChangeText={(text) => handleEdit("otPayRate", text)}
                placeholder="Enter OT pay rate"
                placeholderTextColor={colorScheme === "dark" ? "#666" : "#999"}
                keyboardType="decimal-pad"
              />
            </View>
          </View>

          <View style={styles.rateGrid}>
            <View style={styles.rateField}>
              <Text
                style={[styles.label, colorScheme === "dark" && styles.labelDark]}
              >
                ST Bill Rate:
              </Text>
              <TextInput
                style={[styles.input, colorScheme === "dark" && styles.inputDark]}
                value={editedEmployee.stBillRate?.toString() || ""}
                onChangeText={(text) => handleEdit("stBillRate", text)}
                placeholder="Enter straight-time bill rate"
                placeholderTextColor={colorScheme === "dark" ? "#666" : "#999"}
                keyboardType="decimal-pad"
              />
            </View>
            <View style={styles.rateField}>
              <Text
                style={[styles.label, colorScheme === "dark" && styles.labelDark]}
              >
                OT Bill Rate:
              </Text>
              <TextInput
                style={[styles.input, colorScheme === "dark" && styles.inputDark]}
                value={editedEmployee.otBillRate?.toString() || ""}
                onChangeText={(text) => handleEdit("otBillRate", text)}
                placeholder="Enter overtime bill rate"
                placeholderTextColor={colorScheme === "dark" ? "#666" : "#999"}
                keyboardType="decimal-pad"
              />
            </View>
          </View>

          <View
            style={[
              styles.reminderToggleContainer,
              colorScheme === "dark" && styles.reminderToggleContainerDark,
            ]}
          >
            <Text
              style={[
                styles.reminderToggleText,
                colorScheme === "dark" && styles.reminderToggleTextDark,
              ]}
            >
              Special Format Timesheet
            </Text>
            <Switch
              trackColor={{ false: "#767577", true: "#81b0ff" }}
              thumbColor={
                editedEmployee.isSpecialFormatEmployee ? "#f5dd4b" : "#f4f3f4"
              }
              onValueChange={(value) =>
                handleEdit("isSpecialFormatEmployee", value)
              }
              value={editedEmployee.isSpecialFormatEmployee === true}
              disabled={isLoading}
            />
          </View>

          {/* Supervisors Section */}
          <View style={styles.supervisorsContainer}>
            <Text
              style={[styles.label, colorScheme === "dark" && styles.labelDark]}
            >
              Supervisors:
            </Text>
            {editedEmployee.supervisors?.map((supervisor, index) => (
              <View key={index} style={styles.supervisorRow}>
                <View style={styles.supervisorInput}>
                  <TextInput
                    style={[
                      styles.input,
                      colorScheme === "dark" && styles.inputDark,
                    ]}
                    value={supervisor.id}
                    onChangeText={(text) =>
                      handleSupervisorEdit(index, "id", text)
                    }
                    placeholder="Supervisor ID"
                    placeholderTextColor={
                      colorScheme === "dark" ? "#666" : "#999"
                    }
                  />
                </View>
                <View style={styles.supervisorInput}>
                  <TextInput
                    style={[
                      styles.input,
                      colorScheme === "dark" && styles.inputDark,
                    ]}
                    value={supervisor.email}
                    onChangeText={(text) =>
                      handleSupervisorEdit(index, "email", text)
                    }
                    placeholder="Supervisor Email"
                    placeholderTextColor={
                      colorScheme === "dark" ? "#666" : "#999"
                    }
                  />
                </View>
                <TouchableOpacity
                  onPress={() => removeSupervisor(index)}
                  style={styles.removeSupervisorButton}
                >
                  <AntDesign
                    name="minuscircleo"
                    size={24}
                    color={colorScheme === "dark" ? "#FFFFFF" : "#FF3B30"}
                  />
                </TouchableOpacity>
              </View>
            ))}

            <TouchableOpacity
              onPress={addSupervisor}
              style={styles.addSupervisorButton}
            >
              <AntDesign
                name="pluscircleo"
                size={24}
                color={colorScheme === "dark" ? "#FFFFFF" : "#007AFF"}
              />
              <Text
                style={[
                  styles.addSupervisorText,
                  colorScheme === "dark" && styles.addSupervisorTextDark,
                ]}
              >
                Add Supervisor
              </Text>
            </TouchableOpacity>
          </View>

          {/* Reminder Toggle */}
          <View
            style={[
              styles.reminderToggleContainer,
              colorScheme === "dark" && styles.reminderToggleContainerDark,
            ]}
          >
            <Text
              style={[
                styles.reminderToggleText,
                colorScheme === "dark" && styles.reminderToggleTextDark,
              ]}
            >
              {isReminderEnabled
                ? "Disable Reminder Emails"
                : "Enable Reminder Emails"}
            </Text>
            <Switch
              trackColor={{ false: "#767577", true: "#81b0ff" }}
              thumbColor={isReminderEnabled ? "#f5dd4b" : "#f4f3f4"}
              onValueChange={toggleReminderEmails}
              value={isReminderEnabled}
              disabled={isLoading}
            />
          </View>

          {/* Registration Section */}
          {unregisteredUsers.length > 0 && (
            <View style={styles.registrationContainer}>
              <Text
                style={[
                  styles.registrationTitle,
                  colorScheme === "dark" && styles.registrationTitleDark,
                ]}
              >
                Unregistered Users
              </Text>
              {unregisteredUsers.map((user, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.registrationButton,
                    colorScheme === "dark" && styles.registrationButtonDark,
                  ]}
                  onPress={() => handleRegisterUser(user)}
                >
                  <Text style={styles.registrationButtonText}>
                    Register {user.email}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* Action Buttons */}
          <View style={[styles.buttonSection, colorScheme === "dark" && styles.buttonSectionDark]}>
            <Text style={[styles.sectionTitle, colorScheme === "dark" && styles.sectionTitleDark]}>
              Employee Actions
            </Text>
            
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[
                  styles.saveButton,
                  colorScheme === "dark" && styles.saveButtonDark,
                ]}
                onPress={saveChanges}
              >
                <Text style={styles.buttonText}>Save Changes</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[
                  styles.deleteButton,
                  colorScheme === "dark" && styles.deleteButtonDark,
                ]}
                onPress={deleteEmployee}
              >
                <Text style={styles.buttonText}>Delete Employee</Text>
              </TouchableOpacity>
            </View>
          </View>
          
          {/* Welcome Email Buttons */}
          <View style={[styles.buttonSection, colorScheme === "dark" && styles.buttonSectionDark]}>
            <Text style={[styles.sectionTitle, colorScheme === "dark" && styles.sectionTitleDark]}>
              Send Welcome Emails
            </Text>
            
            <TouchableOpacity
              style={[
                styles.emailButton,
                colorScheme === "dark" && styles.emailButtonDark,
              ]}
              onPress={() => sendWelcomeEmail(null, true)}
            >
              <Text style={styles.buttonText}>Send to Employee ({editedEmployee.email})</Text>
            </TouchableOpacity>
            
            {editedEmployee.supervisors?.map((supervisor, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.emailButton,
                  colorScheme === "dark" && styles.emailButtonDark,
                ]}
                onPress={() => sendWelcomeEmail(supervisor, false, index)}
              >
                <Text style={styles.buttonText}>Send to {supervisor.id} ({supervisor.email})</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>

      <CustomAlert
        visible={alertConfig.visible}
        title={alertConfig.title}
        message={alertConfig.message}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        onClose={() => setAlertConfig((prev) => ({ ...prev, visible: false }))}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F8F9FA",
    width: "100%",
  },
  containerDark: {
    backgroundColor: "#121212",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "white",
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    width: "100%",
  },
  headerDark: {
    backgroundColor: "#1E1E1E",
    borderBottomColor: "#333",
  },
  backButton: {
    padding: 8,
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    color: "#000",
  },
  titleDark: {
    color: "#FFFFFF",
  },
  formContainer: {
    padding: 16,
    width: "100%",
    maxWidth: Platform.OS === "web" ? 800 : "100%",
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
    color: "#000000",
  },
  labelDark: {
    color: "#FFFFFF",
  },
  input: {
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  inputDark: {
    borderColor: "#333",
    backgroundColor: "#1E1E1E",
    color: "#FFFFFF",
  },
  supervisorsContainer: {
    marginBottom: 16,
  },
  supervisorRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    gap: 8,
    flexWrap: "wrap",
  },
  supervisorInput: {
    flex: 1,
    marginRight: 8,
    minWidth: Platform.OS === "web" ? 200 : "100%",
  },
  removeSupervisorButton: {
    padding: 8,
  },
  addSupervisorButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
  },
  addSupervisorText: {
    marginLeft: 8,
    fontSize: 16,
    color: "#007AFF",
  },
  addSupervisorTextDark: {
    color: "#FFFFFF",
  },
  reminderToggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    marginTop: 20,
    width: "100%",
    maxWidth: Platform.OS === "web" ? 500 : "100%",
  },
  reminderToggleContainerDark: {
    backgroundColor: "#333",
    shadowColor: "#FFF",
  },
  reminderToggleText: {
    fontSize: 16,
    fontWeight: "500",
    color: "#4A4A4A",
  },
  reminderToggleTextDark: {
    color: "#FFFFFF",
  },
  registrationContainer: {
    marginTop: 20,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  registrationTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 10,
    color: "#212529",
  },
  registrationTitleDark: {
    color: "#f8f9fa",
  },
  registrationButton: {
    backgroundColor: "#28a745",
    padding: 12,
    borderRadius: 6,
    marginVertical: 5,
  },
  registrationButtonDark: {
    backgroundColor: "#198754",
  },
  registrationButtonText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "500",
    textAlign: "center",
  },
  buttonSection: {
    marginTop: 20,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  buttonSectionDark: {
    backgroundColor: "#1E1E1E",
    borderColor: "#333",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 10,
    color: "#212529",
  },
  sectionTitleDark: {
    color: "#f8f9fa",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  saveButton: {
    backgroundColor: "#4CAF50",
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
    flex: 1,
    marginRight: 8,
  },
  saveButtonDark: {
    backgroundColor: "#388E3C",
  },
  emailButton: {
    backgroundColor: "#2196F3",
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
    width: "100%",
    marginBottom: 8,
  },
  emailButtonDark: {
    backgroundColor: "#1976D2",
  },
  deleteButton: {
    backgroundColor: "#F44336",
    padding: 16,
    borderRadius: 8,
    alignItems: "center",
    flex: 1,
    marginLeft: 8,
  },
  deleteButtonDark: {
    backgroundColor: "#D32F2F",
  },
  buttonText: {
    color: "white",
    fontWeight: "600",
    fontSize: 16,
  },
  formGrid: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    flexWrap: "wrap",
    gap: 16,
    width: "100%",
  },
  formField: {
    flex: Platform.OS === "web" ? 1 : 0,
    width: Platform.OS === "web" ? "48%" : "100%",
    minWidth: Platform.OS === "web" ? 300 : "auto",
  },
  rateGrid: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    flexWrap: "wrap",
    gap: 16,
    width: "100%",
    marginTop: 8,
  },
  rateField: {
    flex: Platform.OS === "web" ? 1 : 0,
    width: Platform.OS === "web" ? "48%" : "100%",
    minWidth: Platform.OS === "web" ? 300 : "auto",
  },
  scrollView: {
    flex: 1,
    width: "100%",
    paddingHorizontal: Platform.OS === "web" ? 0 : 16,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 10,
  },
});

export default EmployeeProfile;
